import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import apiFetch from "../../../../shared/services/apiClient";
import { crearFacturasMasivas } from "../../asientos/hooks/useAsientos";
import SendEmailModal from "../components/SendEmailModal";
import styles from "../styles/addInvoice.module.css";

const fmt = (n) => Number(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2 });

const MESES = [
  "Enero","Febrero","Marzo","Abril","Mayo","Junio",
  "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre",
];

function conceptoAuto(nombre, fechaEmision) {
  if (!nombre) return "";
  let month, year;
  if (fechaEmision) {
    [year, month] = fechaEmision.split("-").map(Number);
    month -= 1;
  } else {
    const d = new Date(); month = d.getMonth(); year = d.getFullYear();
  }
  return `${nombre} — ${MESES[month]} ${year}`;
}

function addDays(dateStr, days) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().split("T")[0];
}

export default function AddFacturaMasiva() {
  const navigate = useNavigate();
  const today    = new Date().toISOString().split("T")[0];

  const [form, setForm] = useState({
    fecha_emision:         today,
    fecha_vencimiento:     addDays(today, 5),
    concepto:              "",
    monto:                 "",
    cuenta_ingreso:        null,
    cuenta_ingreso_nombre: "",
  });

  const [cuentasIngreso, setCuentasIngreso] = useState([]);
  const [filiales,       setFiliales]       = useState([]);
  const [filialIds,      setFilialIds]      = useState(new Set());
  const [search,         setSearch]         = useState("");
  const [errors,         setErrors]         = useState({});
  const [saving,         setSaving]         = useState(false);
  const [apiErr,         setApiErr]         = useState("");
  const [resultado,      setResultado]      = useState(null);
  const [emailModal,     setEmailModal]     = useState(null);

  useEffect(() => {
    // page_size=9999 para traer TODAS las filiales (la API pagina a 8 por defecto)
    apiFetch("/api/residents/filial/?page_size=9999")
      .then((r) => r.json())
      .then((d) => setFiliales(d.results ?? d ?? []));

    apiFetch("/api/finance/catalogo/detalle/?tipo=INGRESO")
      .then((r) => r.json())
      .then((d) => {
        const list = Array.isArray(d) ? d : (d.results ?? []);
        setCuentasIngreso(list);
        if (list.length > 0) {
          const primera = list[0];
          setForm((p) => ({
            ...p,
            cuenta_ingreso:        primera.id,
            cuenta_ingreso_nombre: primera.nombre,
            concepto:              conceptoAuto(primera.nombre, p.fecha_emision),
          }));
        }
      });
  }, []);

  function set(f, v) { setForm((p) => ({ ...p, [f]: v })); }

  function handleFechaEmision(fecha) {
    setForm((p) => ({
      ...p,
      fecha_emision:     fecha,
      fecha_vencimiento: fecha ? addDays(fecha, 5) : "",
      concepto:          conceptoAuto(p.cuenta_ingreso_nombre, fecha),
    }));
  }

  function handleCuentaIngreso(e) {
    const id  = Number(e.target.value);
    const obj = cuentasIngreso.find((c) => c.id === id);
    setForm((p) => ({
      ...p,
      cuenta_ingreso:        id || null,
      cuenta_ingreso_nombre: obj?.nombre ?? "",
      concepto:              conceptoAuto(obj?.nombre ?? "", p.fecha_emision),
    }));
  }

  function toggleFilial(id) {
    setFilialIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleTodas() {
    const vis     = filialesFiltradas;
    const todosOn = vis.every((f) => filialIds.has(f.id));
    setFilialIds((prev) => {
      const next = new Set(prev);
      if (todosOn) vis.forEach((f) => next.delete(f.id));
      else         vis.forEach((f) => next.add(f.id));
      return next;
    });
  }

  const filialesFiltradas = filiales.filter((f) =>
    !search || f.idFilial.toLowerCase().includes(search.toLowerCase())
  );

  function validate() {
    const e = {};
    if (!form.fecha_emision)                    e.fecha_emision    = "Requerida";
    if (!form.fecha_vencimiento)                e.fecha_vencimiento = "Requerida";
    if (!form.concepto.trim())                  e.concepto         = "Requerido";
    if (!form.monto || Number(form.monto) <= 0) e.monto            = "Debe ser mayor a 0";
    if (!form.cuenta_ingreso)                   e.cuenta_ingreso   = "Seleccioná una cuenta de ingreso";
    if (filialIds.size === 0)                   e.filiales         = "Seleccioná al menos una filial";
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleGuardar() {
    if (!validate()) return;
    setSaving(true);
    setApiErr("");
    try {
      const res = await crearFacturasMasivas({
        fecha_emision:     form.fecha_emision,
        fecha_vencimiento: form.fecha_vencimiento,
        concepto:          form.concepto.trim(),
        monto:             Number(form.monto),
        cuenta_ingreso:    form.cuenta_ingreso,
        filiales:          Array.from(filialIds),
      });
      setResultado(res);
    } catch (e) {
      setApiErr(e.message);
    } finally {
      setSaving(false);
    }
  }

  const errMsg = (msg) => msg
    ? <p style={{ color: "#f87171", fontSize: 12, margin: "3px 0 0" }}>{msg}</p>
    : null;

  // ── Pantalla de resultado ─────────────────────────────────────────────────
  if (resultado) {
    return (
      <>
            <span className={styles.inactive}>
              <Link to="/">
                <span className={styles.inactive}>Inicio /</span>
              </Link>
              <Link to="/finanzas/cxc">
                <span className={styles.inactive}> Cuentas por Cobrar /</span>
              </Link>
              <Link to="/finanzas/facturas">
                <span className={styles.inactive}> Facturas / </span>
              </Link>
               <span className={styles.active}>Masiva</span>
            </span>

        <div className="d-flex align-items-center justify-content-center" style={{ margin: "24px 0" }}>
          <div style={{
            background: "#1f2937", border: "1px solid #364250",
            borderRadius: 8, padding: 32, textAlign: "center",
          }}>
            <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>
              {resultado.errores?.length === 0 ? "✅" : "⚠️"}
            </div>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: "#e5ecf5", margin: "0 0 6px" }}>
              {resultado.creadas} factura{resultado.creadas !== 1 ? "s" : ""} creada{resultado.creadas !== 1 ? "s" : ""}
            </h2>

            {resultado.errores?.length > 0 && (
              <p style={{ color: "#fbbf24", fontSize: 14 }}>
                {resultado.errores.length} filial(es) con error
              </p>
            )}

            {resultado.asiento && (
              <div style={{
                margin: "16px auto 0", maxWidth: 380,
                background: "rgba(74,222,128,0.07)",
                border: "1px solid rgba(74,222,128,0.25)",
                borderRadius: 6, padding: "12px 16px", textAlign: "left",
              }}>
                <div style={{ color: "#4ade80", fontWeight: 600, fontSize: 14, marginBottom: 4 }}>
                  <i className="fa-solid fa-book-open" style={{ marginRight: 6 }} />
                  Asiento generado
                </div>
                <div style={{ color: "#8d98a7", fontSize: 13 }}>
                  Número:{" "}
                  <strong style={{ color: "#e5ecf5", fontFamily: "Inter" }}>
                    {resultado.asiento.numero}
                  </strong>
                </div>
                <div style={{ color: "#8d98a7", fontSize: 13, marginTop: 2 }}>
                  Total:{" "}
                  <strong className="monto-typo" style={{ color: "#e5ecf5" }}>₡{fmt(resultado.asiento.total_debitos)}</strong>
                  &nbsp;·&nbsp;{resultado.asiento.n_lineas} líneas
                </div>
              </div>
            )}

            {resultado.errores?.length > 0 && (
              <div style={{
                margin: "12px auto 0", maxWidth: 380,
                background: "rgba(248,113,113,0.07)",
                border: "1px solid rgba(248,113,113,0.25)",
                borderRadius: 6, padding: "10px 14px", textAlign: "left",
              }}>
                <p style={{ color: "#f87171", fontWeight: 600, fontSize: 13, margin: "0 0 4px" }}>
                  Errores:
                </p>
                {resultado.errores.map((e, i) => (
                  <div key={i} style={{ color: "#8d98a7", fontSize: 12, marginBottom: 2 }}>
                    • Filial {e.filial_id}: {e.error}
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", gap: 10, justifyContent: "center",
              marginTop: 24, flexWrap: "wrap" }}>
              <button
                onClick={() => { setResultado(null); setFilialIds(new Set()); }}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
              >
                Crear más facturas
              </button>

              {resultado.facturas?.length > 0 && (
                <button
                  className={`${styles.btn} ${styles["btn-save"]}`}
                  onClick={() => setEmailModal(
                    resultado.facturas.map((f) => ({
                      id_factura:     f.id,
                      numero_factura: f.numero_factura,
                      monto:          f.monto,
                      filial:         { idFilial: f.filial },
                    }))
                  )}
                >
                  <i className="fa-solid fa-envelope" />
                  Enviar correos ({resultado.facturas.length})
                </button>
              )}

              <button
                onClick={() => navigate("/finanzas/facturas")}
                className={`${styles.btn} ${styles["btn-clean-form"]}`}
              >
                Ver Facturas
              </button>
            </div>
          </div>
        </div>

        {emailModal && (
          <SendEmailModal
            facturas={emailModal}
            onClose={() => setEmailModal(null)}
            onSuccess={() => setEmailModal(null)}
          />
        )}
      </>
    );
  }

  // ── Formulario principal ──────────────────────────────────────────────────
  return (
    <>
          <span className={styles.inactive}>
            <Link to="/">
              <span className={styles.inactive}>Inicio /</span>
            </Link>
            <Link to="/finanzas/cxc">
              <span className={styles.inactive}> Cuentas por Cobrar /</span>
            </Link>
            <Link to="/finanzas/facturas">
              <span className={styles.inactive}> Facturas / </span>
            </Link>
             <span className={styles.active}>Masiva</span>
          </span>

      <div style={{ margin: "10px 0 20px" }}>
        <h3 className="title"  style={{ margin: 0, fontWeight: 500, color: "#e5ecf5",
          display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ background: "rgba(249,115,22,0.2)", color: "#f97316",
            padding: "6px 8px", borderRadius: 8 }}>
            <i className="fa-solid fa-layer-group" style={{color: "#f97316"}} />
          </span>
          Facturas Masivas
        </h3>
        <p style={{ margin: "4px 0 0", color: "#8d98a7", fontSize: 16 }}>
          Genera facturas para múltiples filiales
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 360px", gap: 16, alignItems: "start" }}>

        {/* ── Columna izquierda ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

          <div style={{
            background: "#1f2937", border: "1px solid #364250",
            borderRadius: 6, overflow: "hidden",
          }}>
            <div style={{ padding: "12px 16px", borderBottom: "1px solid #364250",
              display: "flex", alignItems: "center", gap: 8 }}>
              <i className="fa-solid fa-file-invoice" style={{ color: "#f97316", fontSize: 18 }} />
              <span className="title" style={{ fontSize: 18, fontWeight: 600, color: "#e5ecf5" }}>
                Datos de la Factura
              </span>
            </div>

            <div className={styles["invoice-details-body"]} style={{ padding: 16 }}>
              <div className="row">
                <div className="col">
                  <label>Fecha Emisión <span style={{ color: "#f87171" }}>*</span></label>
                  <input type="date" className="form-control"
                    style={{ colorScheme: "dark",
                      borderColor: errors.fecha_emision ? "#ef4444" : undefined }}
                    value={form.fecha_emision}
                    onChange={(e) => handleFechaEmision(e.target.value)} />
                  {errMsg(errors.fecha_emision)}
                </div>
                <div className="col">
                  <label>
                    Fecha Vencimiento <span style={{ color: "#f87171" }}>*</span>
                  </label>
                  <input type="date" className="form-control"
                    style={{ colorScheme: "dark",
                      borderColor: errors.fecha_vencimiento ? "#ef4444" : undefined }}
                    value={form.fecha_vencimiento}
                    onChange={(e) => set("fecha_vencimiento", e.target.value)} />
                  {errMsg(errors.fecha_vencimiento)}
                </div>
              </div>

              <div>
                <label>Cuenta de Ingreso (Rubro) <span style={{ color: "#f87171" }}>*</span></label>
                <select className="form-select"
                  value={form.cuenta_ingreso ?? ""}
                  onChange={handleCuentaIngreso}
                  style={{ borderColor: errors.cuenta_ingreso ? "#ef4444" : undefined }}>
                  <option value="">Seleccioná una cuenta…</option>
                  {cuentasIngreso.map((c) => (
                    <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
                  ))}
                </select>
                {errMsg(errors.cuenta_ingreso)}
              </div>

              <div>
                <label className="d-flex align-items-center justify-content-cen" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  Concepto
                  <span className="align-items-center"style={{ fontSize: 11, color: "#8d98a7", fontWeight: 400 }}>auto</span>
                   <span style={{ color: "#f87171" }}>*</span>
                </label>
                <input type="text" className="form-control"
                  placeholder="Se genera automáticamente"
                  value={form.concepto}
                  onChange={(e) => set("concepto", e.target.value)}
                  style={{ borderColor: errors.concepto ? "#ef4444" : undefined }} />
                {errMsg(errors.concepto)}
                <div style={{ fontSize: 12, color: "#8d98a7", marginTop: 3 }}>
                  Generado del rubro y la fecha. Podés editarlo.
                </div>
              </div>

              <div>
                <label>Monto por Filial (₡) <span style={{ color: "#f87171" }}>*</span></label>
                <input type="number" min="1" step="0.01" className="form-control"
                  placeholder="50000"
                  value={form.monto}
                  onChange={(e) => set("monto", e.target.value)}
                  style={{ borderColor: errors.monto ? "#ef4444" : undefined }} />
                {errMsg(errors.monto)}
              </div>
            </div>
          </div>

          {apiErr && (
            <div style={{
              padding: "10px 14px", background: "rgba(239,68,68,0.07)",
              border: "1px solid rgba(239,68,68,0.3)", borderRadius: 6,
              color: "#f87171", fontSize: 14, display: "flex", alignItems: "center", gap: 8,
            }}>
              <i className="fa-solid fa-circle-exclamation" />{apiErr}
            </div>
          )}

          <div className={styles.actions} style={{ justifyContent: "flex-start" }}>
            <Link to="/finanzas/facturas"
              className={`${styles.btn} ${styles["btn-clean-form"]}`}
              style={{ textDecoration: "none" }}>
              Cancelar
            </Link>
            <button onClick={handleGuardar} disabled={saving}
              className={`${styles.btn} ${styles["btn-save"]}`}
              style={{ opacity: saving ? 0.6 : 1 }}>
              {saving
                ? <><i className="fa-solid fa-circle-notch fa-spin" /> Creando…</>
                : <><i className="fa-solid fa-bolt" /> Crear {filialIds.size} factura{filialIds.size !== 1 ? "s" : ""}</>
              }
            </button>
          </div>
        </div>

        {/* ── Columna derecha: filiales ── */}
        <div style={{
          background: "#1f2937", border: "1px solid #364250",
          borderRadius: 6, overflow: "hidden", position: "sticky", top: 16,
        }}>
          <div style={{
            padding: "12px 14px", borderBottom: "1px solid #364250",
            background: "#2a3545",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between",
              alignItems: "center", marginBottom: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 600, color: "#e5ecf5" }}>Filiales</span>
              <span style={{ fontSize: 13, color: "#8d98a7" }}>
                {filialIds.size} / {filiales.length}
              </span>
            </div>

            <div style={{
              display: "flex", alignItems: "center", gap: 7,
              background: "#344152", borderRadius: 6,
              padding: "6px 10px", border: "1px solid #364250", marginBottom: 8,
            }}>
              <i className="fa-solid fa-magnifying-glass"
                style={{ color: "#8d98a7", fontSize: 12 }} />
              <input type="text" placeholder="Buscar filial…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                style={{ flex: 1, background: "none", border: "none",
                  color: "#e5ecf5", fontSize: 14, outline: "none" }} />
            </div>

            <button type="button" onClick={toggleTodas}
              style={{
                width: "100%", padding: "6px", background: "#344152",
                border: "1px solid #364250", borderRadius: 5,
                color: "#8d98a7", cursor: "pointer", fontSize: 13, transition: "color 0.2s",
              }}
              onMouseOver={(e) => (e.currentTarget.style.color = "#e5ecf5")}
              onMouseOut={(e)  => (e.currentTarget.style.color = "#8d98a7")}
            >
              {filialesFiltradas.length > 0 && filialesFiltradas.every((f) => filialIds.has(f.id))
                ? "Deseleccionar todas"
                : "Seleccionar todas"}
            </button>

            {errors.filiales && (
              <p style={{ color: "#f87171", fontSize: 12, margin: "6px 0 0" }}>
                {errors.filiales}
              </p>
            )}
          </div>

          {/* Lista con scroll — muestra TODAS las filiales */}
          <div style={{
            maxHeight: 440, overflowY: "auto",
            scrollbarWidth: "thin", scrollbarColor: "#364250 #1f2937",
          }}>
            {filiales.length === 0 ? (
              <div style={{ padding: 24, textAlign: "center", color: "#8d98a7", fontSize: 13 }}>
                <i className="fa-solid fa-circle-notch fa-spin"
                  style={{ color: "#f97316", display: "block", fontSize: 18, marginBottom: 8 }} />
                Cargando filiales…
              </div>
            ) : filialesFiltradas.length === 0 ? (
              <div style={{ padding: 20, textAlign: "center", color: "#8d98a7", fontSize: 13 }}>
                No hay coincidencias.
              </div>
            ) : filialesFiltradas.map((f, i) => (
              <label key={f.id} style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "9px 14px", cursor: "pointer",
                background: filialIds.has(f.id)
                  ? "rgba(249,115,22,0.1)"
                  : i % 2 === 0 ? "transparent" : "rgba(55,65,81,0.1)",
                borderBottom: "1px solid #364250", transition: "background 0.1s",
              }}>
                <input type="checkbox"
                  checked={filialIds.has(f.id)}
                  onChange={() => toggleFilial(f.id)}
                  style={{ accentColor: "#f97316" }} />
                <span style={{ fontFamily: "Inter", color: "#f97316",
                  fontSize: 12, minWidth: 46 }}>
                  {f.idFilial}
                </span>
                <span style={{ color: "#8d98a7", fontSize: 13, flex: 1 }}>
                  {f.numPlano || "—"}
                </span>
              </label>
            ))}
          </div>

          {filialIds.size > 0 && Number(form.monto) > 0 && (
            <div style={{
              padding: "10px 14px", borderTop: "1px solid #364250",
              display: "flex", justifyContent: "space-between", alignItems: "center",
              background: "#2a3545",
            }}>
              <span style={{ color: "#8d98a7", fontSize: 13 }}>Total estimado</span>
              <span className="monto-typo" style={{ color: "#f97316", fontWeight: 700, fontSize: 14 }}>
                ₡{fmt(Number(form.monto) * filialIds.size)}
              </span>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
